<template>
  <b-card
    no-body
    class="mb-1 contracts-filters"
  >
    <b-card-body>
      <div class="contracts-filters__grid">
        <div class="contracts-filters__period">
          <b-button
            v-b-tooltip.hover.top="'Mês anterior'"
            variant="outline-secondary"
            class="btn-icon"
            aria-label="Mês anterior"
            @click="shiftPeriod(-1)"
          >
            <feather-icon
              icon="ChevronLeftIcon"
              size="16"
            />
          </b-button>
          <div class="contracts-filters__period-field">
            <feather-icon
              icon="CalendarIcon"
              size="16"
              class="contracts-filters__period-icon"
            />
            <flat-pickr
              id="searchCallReminder"
              v-model="contractDate"
              class="form-control"
              :config="flatPickrConfig"
              placeholder="Escolha o período"
              aria-label="Período"
            />
            <span
              v-if="periodTag"
              class="contracts-filters__period-tag"
            >{{ periodTag }}</span>
          </div>
          <b-button
            v-b-tooltip.hover.top="'Próximo mês'"
            variant="outline-secondary"
            class="btn-icon"
            aria-label="Próximo mês"
            @click="shiftPeriod(1)"
          >
            <feather-icon
              icon="ChevronRightIcon"
              size="16"
            />
          </b-button>
        </div>
        <v-select
          id="searchDateReference"
          v-model="dateReference"
          class="contracts-filters__reference"
          :reduce="(reference) => reference.key"
          :options="dateReferences"
          label="name"
          :clearable="false"
          :searchable="false"
        >
          <template #search="{ attributes, events }">
            <input
              class="vs__search"
              v-bind="attributes"
              aria-label="Referência do período"
              v-on="events"
            >
          </template>
          <template #option="{ name, description }">
            <span class="d-block">{{ name }}</span>
            <small class="contracts-filters__option-hint">{{ description }}</small>
          </template>
        </v-select>
        <div class="contracts-filters__status">
          <label
            for="searchStatus"
            class="sr-only"
          >Status</label>
          <contract-filter-select
            id="searchStatus"
            v-model="status"
            :options="statusList"
            take-this="id"
            :loading="loading.statuses"
            placeholder="Todos os status"
          />
        </div>
        <div class="contracts-filters__solution">
          <label
            for="searchFinancialSolution"
            class="sr-only"
          >Solução financeira</label>
          <contract-filter-select
            id="searchFinancialSolution"
            v-model="financialSolution"
            :options="financialSolutions"
            take-this="id"
            :loading="loading.financialSolutions"
            placeholder="Todas as soluções"
          />
        </div>
        <dynamic-select
          id="customerName"
          v-model="customer"
          class="contracts-filters__customer"
          label="Nome do cliente"
          placeholder="Nome do cliente"
          :options="customers"
          :loading="loading.customers"
          @find="findCustomers"
        />
        <v-select
          id="searchStructureOption"
          v-model="structureOption"
          class="contracts-filters__structure"
          :reduce="(structure_list) => structure_list.key"
          :options="structures"
          :loading="loading.structures"
          label="name"
          placeholder="Tipo de visualização"
          :disabled="hasConsultantSelected"
        >
          <template #search="{ attributes, events }">
            <input
              class="vs__search"
              v-bind="attributes"
              aria-label="Tipo de visualização"
              v-on="events"
            >
          </template>
          <template #selected-option="{ name }">
            <span class="contracts-filters__key">Visão</span>{{ name }}
          </template>
          <template #no-options>
            Sem resultados
          </template>
        </v-select>
        <div class="contracts-filters__actions">
          <div class="contracts-filters__consultant">
            <label
              for="searchConsultant"
              class="sr-only"
            >Consultor</label>
            <contract-filter-select
              id="searchConsultant"
              v-model="consultant"
              :options="consultants"
              take-this="id"
              :loading="loading.consultants"
              :disabled="franchise.length > 0"
              placeholder="Todos os consultores"
              @input="consultantChange"
            />
          </div>
          <b-button
            :variant="moreFiltersOpen || secondaryFilterChips.length ? 'outline-primary' : 'outline-secondary'"
            :aria-expanded="moreFiltersOpen ? 'true' : 'false'"
            aria-controls="contracts-more-filters"
            @click="moreFiltersOpen = !moreFiltersOpen"
          >
            <feather-icon
              icon="FilterIcon"
              class="mr-50"
            />
            <span>Mais filtros</span>
            <b-badge
              v-if="secondaryFilterChips.length"
              pill
              variant="primary"
              class="ml-50"
            >{{ secondaryFilterChips.length }}</b-badge>
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="spinnerActive"
            @click="clearSearch"
          >
            Limpar filtros
          </b-button>
          <b-button
            variant="primary"
            class="contracts-filters__search"
            :disabled="spinnerActive"
            @click="search(1)"
          >
            <b-spinner
              v-if="spinnerActive"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="SearchIcon"
              class="mr-50"
            />
            <span>{{ spinnerActive ? 'Aguarde...' : 'Pesquisar' }}</span>
          </b-button>
        </div>
      </div>

      <div
        v-if="secondaryFilterChips.length"
        class="contracts-filters__chips"
        role="group"
        aria-label="Filtros adicionais"
      >
        <span
          v-for="chip in secondaryFilterChips"
          :key="chip.key"
          class="contracts-filters__chip"
          :title="`${chip.label}: ${chip.value}`"
        >
          <span class="contracts-filters__chip-key">{{ chip.label }}</span>
          <span class="contracts-filters__chip-value">{{ chip.value }}</span>
          <button
            type="button"
            class="contracts-filters__chip-remove"
            :disabled="spinnerActive"
            :aria-label="`Remover filtro ${chip.label}`"
            @click="removeSecondaryFilter(chip.key)"
          >
            <feather-icon
              icon="XIcon"
              size="11"
            />
          </button>
        </span>
        <button
          type="button"
          class="contracts-filters__chips-clear"
          :disabled="spinnerActive"
          @click="clearMoreFilters"
        >
          limpar filtros adicionais
        </button>
      </div>

      <b-collapse
        id="contracts-more-filters"
        v-model="moreFiltersOpen"
      >
        <div class="contracts-filters__panel">
          <div class="contracts-filters__groups">
            <section class="contracts-filters__group">
              <h6 class="contracts-filters__group-title">
                <feather-icon
                  icon="UsersIcon"
                  size="13"
                />
                Pessoas
              </h6>
              <div class="contracts-filters__fields">
                <div class="contracts-filters__field">
                  <label for="customerCPF">CPF do cliente</label>
                  <b-form-input
                    id="customerCPF"
                    v-model="customerCPF"
                    v-mask="'###.###.###-##'"
                    @keyup.enter="search(1)"
                  />
                </div>
                <div class="contracts-filters__field">
                  <label for="searchSpecialist">Especialista</label>
                  <contract-filter-select
                    id="searchSpecialist"
                    v-model="specialist"
                    :options="consultants"
                    take-this="id"
                    :loading="loading.consultants"
                    @input="consultantChange"
                  />
                </div>
                <div class="contracts-filters__field">
                  <label for="searchFranchise">Franqueado</label>
                  <contract-filter-select
                    id="searchFranchise"
                    v-model="franchise"
                    :options="franchises"
                    take-this="id"
                    :loading="loading.franchises"
                    @input="consultantChange"
                  />
                </div>
                <div
                  v-if="consultant.length"
                  class="contracts-filters__field"
                >
                  <span
                    id="structureOptionLabel"
                    class="contracts-filters__field-label"
                  >Escopo dos consultores</span>
                  <b-form-radio-group
                    id="structureOption"
                    v-model="partnerStructureOption"
                    :options="partnerStructureOptions"
                    :disabled="franchise.length > 0"
                    aria-labelledby="structureOptionLabel"
                    buttons
                    button-variant="outline-secondary"
                    class="contracts-filters__segmented"
                  />
                </div>
              </div>
            </section>
            <section class="contracts-filters__group">
              <h6 class="contracts-filters__group-title">
                <feather-icon
                  icon="PackageIcon"
                  size="13"
                />
                Produto
              </h6>
              <div class="contracts-filters__fields">
                <div class="contracts-filters__field">
                  <label for="searchPartnerCompany">Empresa</label>
                  <contract-filter-select
                    id="searchPartnerCompany"
                    v-model="partnerCompany"
                    :options="partnerCompanies"
                    take-this="id"
                    :loading="loading.partnerCompanies"
                    @input="partnerCompanyChanged"
                  />
                </div>
                <div class="contracts-filters__field">
                  <label for="searchProduct">Produto</label>
                  <contract-filter-select
                    id="searchProduct"
                    v-model="product"
                    :options="products"
                    take-this="id"
                    :loading="loading.products"
                    @input="productChanged"
                  />
                </div>
                <div class="contracts-filters__field">
                  <label for="searchType">Tipo</label>
                  <contract-filter-select
                    id="searchType"
                    v-model="type"
                    :options="contractTypes"
                    take-this="key"
                    @input="typeChanged"
                  />
                </div>
              </div>
            </section>
            <section class="contracts-filters__group">
              <h6 class="contracts-filters__group-title">
                <feather-icon
                  icon="FileTextIcon"
                  size="13"
                />
                Contrato
              </h6>
              <div class="contracts-filters__fields">
                <div class="contracts-filters__field">
                  <label for="contractID">Id do contrato</label>
                  <b-form-input
                    id="contractID"
                    v-model="contractId"
                    @keyup.enter="search(1)"
                  />
                </div>
                <div class="contracts-filters__field">
                  <label for="searchDaysOnStatus">Dias no status</label>
                  <b-form-input
                    id="searchDaysOnStatus"
                    v-model="daysOnStatus"
                    type="number"
                    @keyup.enter="search(1)"
                  />
                </div>
              </div>
            </section>
          </div>
          <div class="contracts-filters__panel-footer">
            <b-button
              size="sm"
              variant="flat-secondary"
              :disabled="!secondaryFilterChips.length || spinnerActive"
              @click="clearMoreFilters"
            >
              Limpar filtros adicionais
            </b-button>
          </div>
        </div>
      </b-collapse>
    </b-card-body>
  </b-card>
</template>
<script>
import {
  BCard,
  BCardBody,
  BFormInput,
  BFormRadioGroup,
  BButton,
  BSpinner,
  BCollapse,
  BBadge,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr'
import { mapActions, mapGetters } from 'vuex'
import {
  READ_ACTION,
  CREATE_ACTION,
  UPDATE_ACTION,
  DELETE_ACTION,
  CONTRACT_RESOURCE,
} from '@/constants/resources'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import DynamicSelect from '@/modules/shared/components/DynamicSelect.vue'
import { useToast } from 'vue-toastification/composition'
import flatPickr from 'vue-flatpickr-component'
// Flatpickr é a dependência do vue-flatpickr-component já usado por esta tela.
// eslint-disable-next-line import/no-extraneous-dependencies
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import * as sharedTypes from '@/modules/shared/store/types'
import * as accountTypes from '@/modules/account/store/types'
import { OWN, STRUCTURE } from '@/constants/structure_options'
import {
  COMPETENCE,
  CONTRACT_DATE_REFERENCES,
} from '@/constants/contract_date_references'
import { CONTRACT_TYPES } from '@/constants/contract_types'
import * as types from '../store/types'
import ContractFilterSelect from './ContractFilterSelect.vue'
import {
  PERIOD_PRESETS,
  periodLabel,
  periodRange,
  presetKeyOf,
  presetRange,
  shiftMonth,
  toPeriodValue,
} from '../utils/contractPeriod'

export default {
  components: {
    BCard,
    BCardBody,
    BFormInput,
    BFormRadioGroup,
    BButton,
    BSpinner,
    BCollapse,
    BBadge,
    vSelect,
    DynamicSelect,
    ContractFilterSelect,
    flatPickr,
  },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    search: { type: Function, required: true },
    spinnerActive: Boolean,
  },
  data() {
    return {
      loading: {
        list: false,
        customers: false,
        consultants: false,
        statuses: false,
        franchises: false,
        products: false,
        partnerCompanies: false,
        financialSolutions: false,
        structures: false,
      },
      flatPickrConfig: {
        altFormat: 'd/m/Y',
        altInput: true,
        mode: 'range',
        dateFormat: 'Y-m-d',
        locale: Portuguese,
        plugins: [ShortcutButtonsPlugin({
          button: PERIOD_PRESETS.map(preset => ({
            label: preset.label,
            attributes: { class: 'contracts-calendar__preset' },
          })),
          onClick: (index, fp) => this.applyPeriodPreset(PERIOD_PRESETS[index].key, fp),
        })],
        onReady: [(dates, text, fp) => fp.calendarContainer.classList.add('contracts-calendar')],
        onOpen: [(dates, text, fp) => this.markActivePeriodPreset(fp)],
      },
      clearSelect: null,
      customer: null,
      customerCPF: null,
      contractId: null,
      status: [],
      contractDate: null,
      dateReference: COMPETENCE,
      initialPaymentDate: null,
      endPaymentDate: null,
      franchise: [],
      consultant: [],
      specialist: [],
      partnerStructureOption: STRUCTURE,
      partnerStructureOptions: [
        { value: STRUCTURE, text: 'Estrutura' },
        { value: OWN, text: 'Próprio' },
      ],
      daysOnStatus: null,
      product: [],
      type: [],
      contractTypes: CONTRACT_TYPES,
      partnerCompany: [],
      financialSolution: [],
      structureOption: null,
      timer: null,
      moreFiltersOpen: false,
      knownFilterNames: {},
      dateReferences: CONTRACT_DATE_REFERENCES,
    }
  },
  setup() {
    return { toast: useToast() }
  },
  computed: {
    ...mapGetters({
      customers: sharedTypes.CUSTOMERS,
      consultants: types.CONSULTANTS,
      statusList: types.STATUS_LIST,
      products: sharedTypes.PRODUCTS,
      partnerCompanies: sharedTypes.PARTNER_COMPANIES,
      financialSolutions: types.FINANCIAL_SOLUTIONS,
      user: accountTypes.USER,
      franchises: sharedTypes.FRANCHISES,
      structures: sharedTypes.STRUCTURE_TYPES,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    readContractEnabled() {
      return this.$can(READ_ACTION, CONTRACT_RESOURCE)
    },
    createContractEnabled() {
      return this.$can(CREATE_ACTION, CONTRACT_RESOURCE)
    },
    updateContractEnabled() {
      return this.$can(UPDATE_ACTION, CONTRACT_RESOURCE)
    },
    deleteContractEnabled() {
      return this.$can(DELETE_ACTION, CONTRACT_RESOURCE)
    },
    hasConsultantSelected() {
      return (
        (this.consultant && this.consultant.length > 0)
        || (this.franchise && this.franchise.length > 0)
        || (this.specialist && this.specialist.length > 0)
      )
    },
    periodTag() {
      const { startDate, endDate } = periodRange(this.contractDate)
      return periodLabel(startDate, endDate)
    },
    secondaryFilterChips() {
      const fields = [
        ['customerCPF', 'CPF', null], ['contractId', 'ID do contrato', null],
        ['daysOnStatus', 'Dias no status', null],
        ['franchise', 'Franqueado', this.franchises], ['specialist', 'Especialista', this.consultants],
        ['partnerCompany', 'Empresa', this.partnerCompanies], ['product', 'Produto', this.products],
        ['type', 'Tipo', this.contractTypes],
      ]
      const chips = fields.filter(([key]) => (Array.isArray(this[key]) ? this[key].length : this[key] !== null && this[key] !== '' && this[key] !== undefined))
        .map(([key, label, options]) => ({
          key,
          label,
          value: Array.isArray(this[key]) ? this[key].map(id => {
            const option = (options || []).find(item => String(item[key === 'type' ? 'key' : 'id']) === String(id))
            return option ? option.name : (this.knownFilterNames[`${key}:${id}`] || id)
          }).join(', ') : this[key],
        }))
      if (this.consultant.length && this.partnerStructureOption !== STRUCTURE) {
        chips.push({ key: 'partnerStructureOption', label: 'Escopo dos consultores', value: 'Próprio' })
      }
      return chips
    },
  },
  watch: {
    // Guarda o rótulo: a seleção sai da lista quando o filtro pai a reduz.
    products: {
      immediate: true,
      handler(list) { this.cacheFilterNames('product', list) },
    },
  },
  mounted() {
    this.setDefaultContractDate()
    this.itemsPerPage = this.initialItemsPerPage
    this.loading.statuses = true
    this.getStatusList()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar os status para seleção. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        this.loading.statuses = false
      })
    this.loading.consultants = true
    this.getConsultants()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar os consultores para seleção. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        this.loading.consultants = false
      })
    this.loading.franchises = true
    this.getFranchises()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar os franqueados para seleção. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        this.loading.franchises = false
      })
    this.loading.products = true
    // Tira EP e as assinaturas da entrevista das opções de Produto: elas passam a
    // ser oferecidas pelo filtro Tipo. É opt-in, as outras telas seguem iguais.
    this.getProducts({ has_financial_solution: 0, exclude_contract_type_products: 1 })
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar os produtos para seleção. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        this.loading.products = false
      })
    this.loading.partnerCompanies = true
    this.getPartnerCompanies({ has_financial_solution: 0 })
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar as empresas para seleção. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        this.loading.partnerCompanies = false
      })
    this.loading.financialSolutions = true
    this.getFinancialSolutions()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar as soluções financeiras para seleção. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        this.loading.financialSolutions = false
      })
    this.getStructures()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar as estruturas para seleção. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        this.loading.structures = false
      })
  },
  methods: {
    cacheFilterNames(field, list) {
      (list || []).forEach(item => {
        if (item.name) this.$set(this.knownFilterNames, `${field}:${item.id}`, item.name)
      })
    },
    clearMoreFilters() {
      this.customerCPF = null
      this.contractId = null
      this.daysOnStatus = null
      this.franchise = []
      this.specialist = []
      this.partnerCompany = []
      this.product = []
      this.type = []
      this.partnerStructureOption = STRUCTURE
      this.partnerCompanyChanged([])
      this.loadFinancialSolutions([], [])
      this.search(1)
    },
    removeSecondaryFilter(key) {
      if (key === 'partnerStructureOption') this[key] = STRUCTURE
      else this[key] = Array.isArray(this[key]) ? [] : null
      if (key === 'partnerCompany') this.partnerCompanyChanged([])
      if (key === 'product') this.loadFinancialSolutions(this.partnerCompany, [])
      this.search(1)
    },
    loadFinancialSolutions(companies, products) {
      return this.getFinancialSolutions({ partner_company_id: companies, product_id: products }).catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!', text: 'Não foi possível carregar as soluções financeiras.', variant: 'danger',
          },
        })
      })
    },
    shiftPeriod(step) {
      this.contractDate = toPeriodValue(shiftMonth(periodRange(this.contractDate).endDate, step))
    },
    applyPeriodPreset(key, fp) {
      const { startDate, endDate } = presetRange(key)
      fp.setDate([startDate, endDate], true)
      fp.close()
    },
    markActivePeriodPreset(fp) {
      const { startDate, endDate } = periodRange(this.contractDate)
      const activeKey = presetKeyOf(startDate, endDate)
      fp.calendarContainer.querySelectorAll('.contracts-calendar__preset').forEach((button, index) => {
        button.classList.toggle('is-active', PERIOD_PRESETS[index].key === activeKey)
      })
    },
    ...mapActions({
      searchContracts: types.SEARCH_CONTRACTS,
      getConsultants: types.GET_CONSULTANTS,
      getStatusList: types.GET_STATUS_LIST,
      getProducts: sharedTypes.GET_PRODUCTS,
      getPartnerCompanies: sharedTypes.GET_PARTNER_COMPANIES,
      getFinancialSolutions: types.GET_FINANCIAL_SOLUTIONS,
      getCustomers: sharedTypes.GET_CUSTOMERS,
      getFranchises: sharedTypes.GET_FRANCHISES,
      getStructures: sharedTypes.GET_STRUCTURE_TYPES,
    }),
    findCustomers(keyword) {
      this.loading.customers = true
      this.getCustomers({ keyword })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao carregar os clientes para seleção. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.loading.customers = false
        })
    },
    partnerCompanyChanged(partnerCompany) {
      if (partnerCompany) {
        this.getProducts({
          has_financial_solution: 0,
          partner_company_id: partnerCompany,
          exclude_contract_type_products: 1,
        }).catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao carregar os produtos. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
      }
    },
    // Grupos distintos: Produto já exclui EP e assinaturas, então combinar os dois
    // daria sempre resultado vazio. Um limpa o outro.
    typeChanged(type) {
      if (type && type.length > 0) {
        this.product = []
        this.loadFinancialSolutions(this.partnerCompany, [])
      }
    },
    productChanged(product) {
      if (product && product.length > 0) {
        this.type = []
      }
      if (product) {
        this.getFinancialSolutions({
          partner_company_id: this.partnerCompany,
          product_id: product,
        }).catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao carregar os produtos. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
      }
    },
    consultantChange() {
      if (this.hasConsultantSelected) {
        this.structureOption = undefined
      }
    },
    setDefaultContractDate() {
      this.contractDate = toPeriodValue(presetRange('month'))
    },
    clearSearch() {
      this.customer = null
      this.customerCPF = null
      this.status = []
      this.contractDate = null
      this.dateReference = COMPETENCE
      this.franchise = []
      this.consultant = []
      this.specialist = []
      this.partnerStructureOption = STRUCTURE
      this.structureOption = null
      this.daysOnStatus = null
      this.product = []
      this.type = []
      this.partnerCompany = []
      this.financialSolution = []
      this.clearSelect += 1
      this.contractId = null

      this.setDefaultContractDate()
      this.partnerCompanyChanged([])
      this.loadFinancialSolutions([], [])

      this.search(1)
    },
  },
}
</script>

<style lang="scss" scoped>
.contracts-filters {
  --filters-muted: #b9b9c3;
  --filters-chip-x: #82868b;
  --filters-input-bg: #fff;
  --filters-input-border: #d8d6de;
  --filters-tag-fg: #ca390d;
  --filters-border: rgba(34, 41, 47, 0.08);
  --filters-rule: #ebe9f1;
  --filters-chip-bg: #f3f2f7;
  --filters-chip-fg: #5e5873;
  --filters-chip-x-hover: #e4e2ec;
  --filters-link-hover: #6e6b7b;
}

body.dark-layout .contracts-filters {
  --filters-muted: #676d7d;
  --filters-chip-x: #676d7d;
  --filters-input-bg: #283046;
  --filters-input-border: #404656;
  --filters-tag-fg: #f5a081;
  --filters-border: rgba(255, 255, 255, 0.14);
  --filters-rule: rgba(255, 255, 255, 0.14);
  --filters-chip-bg: rgba(255, 255, 255, 0.06);
  --filters-chip-fg: #d0d2d6;
  --filters-chip-x-hover: rgba(255, 255, 255, 0.12);
  --filters-link-hover: #d0d2d6;
}

.form-group {
  margin-bottom: 0;
}

.contracts-filters__grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  grid-template-areas:
    "period period reference status solution"
    "customer structure actions actions actions";
  gap: 0.75rem;
  align-items: start;
}

.contracts-filters__grid ::v-deep .vs--single .vs__selected-options {
  flex-wrap: nowrap;
  min-width: 0;
}

.contracts-filters__grid ::v-deep .vs--single .vs__selected {
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.contracts-filters__period {
  grid-area: period;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.contracts-filters__period-field {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  min-width: 0;
  height: 2.714rem;
  border: 1px solid var(--filters-input-border);
  border-radius: 0.357rem;
  background: var(--filters-input-bg);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.contracts-filters__period-field:focus-within {
  border-color: #f26237;
  box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);
}

.contracts-filters__period-icon {
  flex: none;
  margin-left: 0.8rem;
  color: var(--filters-muted);
  pointer-events: none;
}

.contracts-filters__period-field ::v-deep .form-control {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  padding: 0 0.5rem 0 0.6rem;
  border: 0;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}

.contracts-filters__period-tag {
  flex: none;
  margin-right: 0.55rem;
  padding: 0.1rem 0.55rem;
  border-radius: 1rem;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  background: rgba(242, 98, 55, 0.12);
  color: var(--filters-tag-fg);
  pointer-events: none;
}

.contracts-filters__reference {
  grid-area: reference;
  min-width: 0;
}

.contracts-filters__option-hint {
  display: block;
  white-space: normal;
  opacity: 0.8;
}

.contracts-filters__status {
  grid-area: status;
  min-width: 0;
}

.contracts-filters__solution {
  grid-area: solution;
  min-width: 0;
}

.contracts-filters__customer {
  grid-area: customer;
  min-width: 0;
}

.contracts-filters__customer ::v-deep label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.contracts-filters__structure {
  grid-area: structure;
  min-width: 0;
}

.contracts-filters__key {
  margin-right: 0.35rem;
  color: var(--filters-muted);
}

.contracts-filters__actions {
  grid-area: actions;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
}

.contracts-filters__actions .btn {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  height: 2.714rem;
}

.contracts-filters__consultant {
  flex: 1 1 12rem;
  min-width: 0;
}

.contracts-filters__search {
  min-width: 8.5rem;
}

.contracts-filters__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.contracts-filters__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.44rem;
  max-width: 100%;
  height: 28px;
  padding: 0 0.375rem 0 0.7rem;
  border-radius: 20px;
  background: var(--filters-chip-bg);
  font-size: 0.775rem;
  font-weight: 500;
  color: var(--filters-chip-fg);
  white-space: nowrap;
}

.contracts-filters__chip-key {
  flex: none;
  color: var(--filters-muted);
  font-weight: 400;
}

.contracts-filters__chip-value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contracts-filters__chip-remove {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--filters-chip-x);
  cursor: pointer;
}

.contracts-filters__chip-remove:hover {
  background: var(--filters-chip-x-hover);
}

.contracts-filters__chips-clear {
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.775rem;
  color: var(--filters-muted);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.contracts-filters__chips-clear:hover {
  color: var(--filters-link-hover);
}

.contracts-filters__chip-remove:disabled,
.contracts-filters__chips-clear:disabled {
  opacity: 0.5;
  cursor: default;
}

.contracts-filters__panel {
  margin-top: 1rem;
  border: 1px solid var(--filters-border);
  border-radius: 0.5rem;
}

.contracts-filters__groups {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.75rem;
  padding: 1rem 1rem 1.15rem;
}

.contracts-filters__group {
  flex: 1 1 300px;
  min-width: 0;
}

.contracts-filters__group-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.85rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px solid var(--filters-rule);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--filters-muted);
}

.contracts-filters__fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.contracts-filters__field {
  min-width: 0;
}

.contracts-filters__field label,
.contracts-filters__field-label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.3rem;
  font-size: 0.8rem;
  font-weight: 500;
}

.contracts-filters__segmented {
  display: flex;
}

.contracts-filters__segmented ::v-deep .btn {
  flex: 1 1 0;
  min-width: 0;
  padding-right: 0.25rem;
  padding-left: 0.25rem;
  font-size: 0.857rem;
  white-space: nowrap;
}

.contracts-filters__segmented ::v-deep .btn.active:not(.disabled),
.contracts-filters__segmented ::v-deep .btn:not(:disabled):not(.disabled).active {
  background-color: #f26237;
  border-color: #f26237;
  color: #fff !important;
  box-shadow: none;
}

.contracts-filters__panel-footer {
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 0.75rem;
  border-top: 1px solid var(--filters-border);
}

@media (max-width: 1499.98px) {
  .contracts-filters__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-areas:
      "period period reference customer"
      "status status solution solution"
      "structure actions actions actions";
  }
}

@media (max-width: 991.98px) {
  .contracts-filters__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
      "period period"
      "reference structure"
      "status solution"
      "customer customer"
      "actions actions";
  }
}

@media (max-width: 575.98px) {
  .contracts-filters__grid {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "period"
      "reference"
      "status"
      "solution"
      "customer"
      "structure"
      "actions";
  }

  .contracts-filters__period-icon,
  .contracts-filters__period-tag {
    display: none;
  }

  .contracts-filters__actions {
    flex-wrap: wrap;
  }

  .contracts-filters__actions .btn {
    flex: 1 1 auto;
  }

  .contracts-filters__consultant {
    flex-basis: 100%;
  }

  .contracts-filters__fields {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.contracts-calendar {
  --contracts-calendar-rule: #ebe9f1;
  --contracts-calendar-border: #d8d6de;

  .shortcut-buttons-flatpickr-wrapper {
    display: block;
    padding: 0.65rem 0.6rem 0.75rem;
    border-top: 1px solid var(--contracts-calendar-rule);
  }

  .shortcut-buttons-flatpickr-buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.4rem;
  }

  .contracts-calendar__preset {
    height: 28px;
    margin: 0;
    padding: 0 0.75rem;
    border: 1px solid var(--contracts-calendar-border);
    border-radius: 14px;
    background: transparent;
    font-size: 0.8rem;
    color: inherit;
    cursor: pointer;

    &:hover {
      border-color: #f26237;
      color: #f26237;
    }

    &.is-active {
      border-color: #f26237;
      background: #f26237;
      color: #fff;
      font-weight: 600;
    }
  }
}

.dark-layout .contracts-calendar {
  --contracts-calendar-rule: rgba(255, 255, 255, 0.08);
  --contracts-calendar-border: rgba(255, 255, 255, 0.14);
}
</style>
